import type { AppRoute } from '../../routing/route';
import {
  getLandingPath,
  getPlayPath,
  getDesignPath,
  getDevelopmentPath,
  getTestingPath,
  getArchitecturePath,
} from '../../routing/route';
import type { ArticleSection } from './article-content';
import { getArticleDefinition } from './article-content';

export const renderArticleView = (
  container: HTMLElement,
  route: AppRoute,
  onNavigate: (path: string) => void,
): void => {
  const article = getArticleDefinition(route);
  if (!article) {
    container.innerHTML = '<div class="article-error">記事が見つかりませんでした。</div>';
    return;
  }

  const landingPath = getLandingPath();
  const playPath = getPlayPath();
  const designPath = getDesignPath();
  const developmentPath = getDevelopmentPath();
  const testingPath = getTestingPath();
  const architecturePath = getArchitecturePath();

  const renderSection = (section: ArticleSection): string => {
    const paragraphsHtml = section.paragraphs.map((p) => `<p>${p}</p>`).join('');
    const listHtml = section.listItems
      ? `<ul class="article-list">${section.listItems.map((item) => `<li>${item}</li>`).join('')}</ul>`
      : '';

    const subsectionsHtml = section.subsections
      ? section.subsections
          .map((sub) => {
            const subParagraphs = sub.paragraphs.map((p) => `<p>${p}</p>`).join('');
            const subList = sub.listItems
              ? `<ul class="article-list">${sub.listItems.map((item) => `<li>${item}</li>`).join('')}</ul>`
              : '';
            const subCode = sub.codeSnippet
              ? `<pre class="article-code"><code>${sub.codeSnippet}</code></pre>`
              : '';
            return `
              <div class="article-subsection">
                <h3>${sub.title}</h3>
                ${subParagraphs}
                ${subList}
                ${subCode}
              </div>
            `;
          })
          .join('')
      : '';

    return `
      <section class="article-section">
        <h2>${section.title}</h2>
        ${paragraphsHtml}
        ${listHtml}
        ${subsectionsHtml}
      </section>
    `;
  };

  const sectionsHtml = article.sections.map(renderSection).join('');

  container.innerHTML = `
    <div class="article-page">
      <nav class="article-top-nav" aria-label="Article Top Navigation">
        <a href="${landingPath}" class="article-nav-link" data-nav="${landingPath}">← OFFICE CLASH トップへ戻る</a>
        <a href="${playPath}" class="article-play-button" data-nav="${playPath}">無料でプレイ</a>
      </nav>

      <header class="article-header">
        <p class="article-badge">OFFICE CLASH 技術資料</p>
        <h1 class="article-title">${article.title}</h1>
        <p class="article-subtitle">${article.subtitle}</p>
        <p class="article-lead">${article.lead}</p>
      </header>

      <main class="article-body">
        ${sectionsHtml}
      </main>

      <section class="article-cross-nav" aria-label="Technical Articles Navigation">
        <h2>その他の技術記事</h2>
        <div class="article-cross-grid">
          <a href="${designPath}" class="article-cross-card ${route === 'DESIGN' ? 'current' : ''}" data-nav="${designPath}">
            <span class="article-cross-tag">Design</span>
            <h3>ゲームデザイン</h3>
            <p>2キャラクターの特性と棒人間ビジュアル設計</p>
          </a>
          <a href="${developmentPath}" class="article-cross-card ${route === 'DEVELOPMENT' ? 'current' : ''}" data-nav="${developmentPath}">
            <span class="article-cross-tag">Development</span>
            <h3>開発工程</h3>
            <p>Phase 0からの実装タイムラインとCore設計</p>
          </a>
          <a href="${testingPath}" class="article-cross-card ${route === 'TESTING' ? 'current' : ''}" data-nav="${testingPath}">
            <span class="article-cross-tag">Testing</span>
            <h3>テスト戦略</h3>
            <p>決定論的シミュレーションと多層自動テスト</p>
          </a>
          <a href="${architecturePath}" class="article-cross-card ${route === 'ARCHITECTURE' ? 'current' : ''}" data-nav="${architecturePath}">
            <span class="article-cross-tag">Architecture</span>
            <h3>アーキテクチャ</h3>
            <p>Core/Presentation分離とCloudflare配信基盤</p>
          </a>
        </div>
      </section>

      <footer class="article-footer">
        <div class="article-footer-cta">
          <a href="${playPath}" class="landing-cta-button" data-nav="${playPath}">ゲームをプレイする</a>
        </div>
        <p>&copy; 2026 promptlab-games.com / デジガーデン</p>
      </footer>
    </div>
  `;

  // Attach SPA navigation listeners to internal data-nav links
  const links = container.querySelectorAll<HTMLAnchorElement>('a[data-nav]');
  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const targetPath = link.getAttribute('data-nav') ?? landingPath;
      onNavigate(targetPath);
    });
  });
};
